import { useDispatch } from 'react-redux';
import { Applicant, BlockType, PageType, RelationshipType } from '@common/entities';
import { EntrataSession } from 'auth/EntrataSession';
import { setApplicationScreening } from 'state/slices/applicationScreeningSlice';
import { setApplicationSettings } from 'state/slices/applicationSettingsSlice';
import { setApplicationData } from 'state/slices/applicationSlice';
import { setIsPreviewMode } from 'state/slices/authSlice';
import {
  replaceExternalSyncErrorsForTab,
  setActivePageId,
  setCurrentTabIndex,
  setLeasingJourneyError,
  updateLeasingJourneyErrors,
} from 'state/slices/navigationSlice';
import { updateResponseState } from 'state/slices/responseSlice';
import {
  updateAddon,
  updateSelectedSpecials,
  updateUnitSelection,
  updateWaitlistSelections,
} from 'state/slices/unitSelectionSlice';
import { store } from 'state/store';

// import { useCheckToShowPaymentsPage } from 'hooks/payments';
import {
  getApplicationForm,
  getCustomerRelationships,
  getResponse,
  getScreeningConditions,
} from 'services';
import type { Form } from 'types/form';
import { getTodaysDate } from 'utils/date';
import { prepareFormInitializingData } from 'utils/form';
import { getActiveResponse } from 'utils/getActiveResponse';
import { getDisplayableExternalSyncErrors } from 'utils/getExternalSyncErrors';
import { getFilteredErrors } from 'utils/getFilteredErrors';
import handleError from 'utils/handleError';
import { getApplicantCustomerType } from 'utils/leadProfileUtils';
import { getPageIndex } from 'utils/page-utils';
import { mergePropertyIdentificationConfig } from 'utils/propertyIdentification';

import { getSecondaryApplicantStoreJson } from '../utils/getSecondaryApplicantStoreJson';

import useInitializeQuotes from './quotes/useInitializeQuotes';
import { useSetFloorPlanPageDataInStore } from './floor-plans';
import { useInitializeIDIncomeVerification } from './id-income-verification';
import { useFilterPagesAndFields } from './useFilterPagesAndFields';
import { useGetPropertySettings } from './useGetPropertySettings';
import { useInitializeSpecials } from './useInitializeSpecials';

export type LJInitMode = 'full' | 'screeningMinimal';

type Props = {
  applicant: Applicant;
  form: Form;
  responseId?: string;
  initMode?: LJInitMode;
  savedResponse?: Awaited<ReturnType<typeof getResponse>>['data'];
};

async function ensureScreeningConditionsInStore(responseId: string) {
  if (store.getState().applicationScreening.conditionSets.length > 0) {
    return;
  }
  try {
    const res = await getScreeningConditions(responseId);
    const conditionSets = res.data.conditionSets ?? [];
    if (conditionSets.length === 0) {
      return;
    }
    store.dispatch(
      setApplicationScreening({
        conditionSets,
        subsetHeadings: {},
        requiredItems: null,
        selectedFinancialConditions: [],
        applicantName: '',
        applicationName: '',
        confirmationMessage: '',
      }),
    );
  } catch (e) {
    handleError(
      `Form Viewer - Error loading screening conditions: ${e}`,
      (e as Error)?.message ?? 'Error loading screening conditions',
    );
  }
}

export const useInitializeLJApplication = () => {
  const dispatch = useDispatch();

  const initializeFloorPlanPageData = useSetFloorPlanPageDataInStore();
  const initializePropertySettings = useGetPropertySettings();
  // const checkToShowPaymentsPage = useCheckToShowPaymentsPage();
  const initializeQuotes = useInitializeQuotes();
  const initializeSpecials = useInitializeSpecials();
  const initializeIDVerificationStore = useInitializeIDIncomeVerification(false);
  const { hidePagesAndFields } = useFilterPagesAndFields();

  function updateAuthStore() {
    dispatch(setIsPreviewMode(false));
  }

  /**
   * Hydrates unit / floor plan / waitlist Redux state from the saved `response` blob.
   * Still runs when the Floor Plans step is absent from `form.pages` (e.g. co-applicant
   * forms) if `floorPlanData` or waitlist selections exist so the cart can show the home context.
   */
  async function initializeFloorPlanStore(
    propertyId: number,
    { pages }: Form,
    response: any,
    leaseId: number,
  ) {
    const pageIndex = getPageIndex(pages, PageType.FloorPlans);
    // For secondary applicant, there won't be a floorplan page, but they still should have floorplan data from the primary applicant.
    const { floorPlanData, waitlistSelections } = response;
    const hasWaitlistFloorplans =
      waitlistSelections &&
      typeof waitlistSelections === 'object' &&
      Array.isArray(waitlistSelections.floorplanIds) &&
      waitlistSelections.floorplanIds.length > 0;
    const hasRestorableSelection = !!floorPlanData || hasWaitlistFloorplans;

    if (pageIndex < 0 && !hasRestorableSelection) {
      return;
    }

    if (!floorPlanData && !waitlistSelections) {
      const todaysDate = getTodaysDate();
      dispatch(updateUnitSelection({ moveInDate: todaysDate }));
      return;
    }

    const {
      floorplanId,
      unitSpaceId,
      termMonth,
      moveInDate,
      spaceConfigurationId,
      leaseTermId,
      leaseStartWindowId,
      preferredAmenityIds,
      isStudentLaunch,
    } = floorPlanData || {};

    let moveInDateToUse = '';
    if (!moveInDate) {
      moveInDateToUse = getTodaysDate();
    } else {
      const todaysDate = getTodaysDate();
      const parsedTodaysDate = Date.parse(todaysDate);
      const parsedMoveInDate = Date.parse(moveInDate);
      moveInDateToUse = parsedMoveInDate > parsedTodaysDate ? moveInDate : todaysDate;
    }

    const errorCount = await initializeFloorPlanPageData(
      moveInDateToUse,
      floorplanId,
      unitSpaceId,
      termMonth,
      propertyId,
      leaseId,
      waitlistSelections,
      spaceConfigurationId,
      leaseTermId,
      leaseStartWindowId, // NEW parameter
      preferredAmenityIds,
      isStudentLaunch,
    );
    if (pageIndex >= 0) {
      dispatch(
        updateLeasingJourneyErrors({
          pageIndex,
          count: errorCount,
        }),
      );
    }
  }

  function initializeResponseStore(response: any) {
    const formValue = prepareFormInitializingData(response) as any;
    dispatch(updateResponseState(formValue));
    return formValue;
  }

  function initializeNavigationStore({ pages }: Form, response: any) {
    const parsedResponses = JSON.parse(response.details);
    const { filteredPages, stepsStatus } = getFilteredErrors(pages, parsedResponses);

    dispatch(setCurrentTabIndex(0));
    const activePageId = parsedResponses?.pages?.find((page: any) => page.isCurrentPage === true);

    if (activePageId) dispatch(setActivePageId(activePageId.pageId));

    dispatch(setLeasingJourneyError({ pages: filteredPages, errors: stepsStatus }));
    const errorsByPageId = (parsedResponses?.pages ?? []).reduce(
      (errors: Record<string, { operation: string; message: string }[]>, page: any) => {
        const externalSyncErrors = getDisplayableExternalSyncErrors(
          page.externalSync,
          page.errorCount,
        );
        if (externalSyncErrors.length > 0) {
          return {
            ...errors,
            [page.pageId]: externalSyncErrors,
          };
        }
        return errors;
      },
      {},
    );
    dispatch(replaceExternalSyncErrorsForTab({ tabIndex: 0, errorsByPageId }));
  }

  async function initializeApplicationStore(
    applicant: Applicant,
    formData: Form,
    responseId: string,
  ) {
    // await checkToShowPaymentsPage(responseId);
    const hideObj = {
      hidePageTypes: [] as PageType[],
      hideBlockTypes: [] as BlockType[],
    };

    const { isIdVerificationEnabled, isIncomeVerificationEnabled } =
      await initializeIDVerificationStore(formData.pages);
    if (!isIdVerificationEnabled) hideObj.hideBlockTypes.push(BlockType.IDVerification);

    if (!isIncomeVerificationEnabled) hideObj.hideBlockTypes.push(BlockType.IncomeVerification);

    const updatedPages = hidePagesAndFields(formData.pages, hideObj);

    const updatedForm = await checkToHideSecondaryApplicantPages(
      applicant,
      { ...formData, pages: updatedPages },
      responseId,
    );
    dispatch(
      setApplicationData({
        applicant,
        form: updatedForm,
        activeResponseId: responseId,
      }),
    );
    dispatch(setApplicationSettings(updatedForm?.settings ?? {}));

    return updatedForm;
  }

  async function checkToHideSecondaryApplicantPages(
    applicant: Applicant,
    formData: Form,
    responseId: string,
  ): Promise<Form> {
    const activeResponse = getActiveResponse(applicant, responseId);
    if (
      activeResponse?.relationshipType === undefined ||
      activeResponse.relationshipType === RelationshipType.Primary
    ) {
      return formData;
    }

    let relationships: Awaited<ReturnType<typeof getCustomerRelationships>>['data'];
    if (
      activeResponse.relationshipType === RelationshipType.Other &&
      activeResponse.entrataCustomerRelationshipId
    ) {
      try {
        relationships = (await getCustomerRelationships()).data;
      } catch {
        // The shared visibility policy limits unresolved occupants to personal info.
        handleError('Form Viewer - Customer relationships unavailable; using limited interview');
      }
    }
    const customerType = getApplicantCustomerType(
      responseId,
      activeResponse.entrataCustomerRelationshipId,
      relationships,
    );
    const secondaryStore = getSecondaryApplicantStoreJson(
      formData,
      applicant,
      responseId,
      activeResponse.relationshipType,
      customerType,
    );
    return secondaryStore.application.form;
  }

  const initializeOffersPage = async ({ pages }: Form, responseData: any) => {
    const isOffersPageAvailable = getPageIndex(pages, PageType.Offers);
    if (isOffersPageAvailable < 0) return;
    const { quote } = responseData;
    if (!quote) return;

    const { quoteId, leaseTermId, unitSpaceId = null } = quote;
    await initializeQuotes(pages, quoteId, leaseTermId, unitSpaceId);
  };

  const initializeUnitSelection = async (
    propertyId: number,
    form: Form,
    response: any,
    leaseId: number,
  ) => {
    const { quotes, floorPlanData } = response;
    if (!quotes) {
      await initializeFloorPlanStore(propertyId, form, response, leaseId);
    }
    if (!floorPlanData) await initializeOffersPage(form, response);
  };

  const initializeAddons = (response: any) => {
    const { addOns, selectedSpecials } = response;
    if (addOns) dispatch(updateAddon(addOns));
    if (selectedSpecials) dispatch(updateSelectedSpecials(selectedSpecials));
  };

  const initializeWaitlistSelections = (response: any) => {
    const { waitlistSelections } = response;
    if (waitlistSelections) {
      dispatch(updateWaitlistSelections({ waitlistSelections }));
    }
  };

  async function initializeStore(
    applicant: Applicant,
    formData: Form,
    userResponse: any,
    responseId: string,
  ) {
    const updatedForm = await initializeApplicationStore(applicant, formData, responseId);
    initializeNavigationStore(updatedForm, userResponse);
    const responseObject = initializeResponseStore(userResponse);

    const { propertyId } = applicant;
    const activeResponse = getActiveResponse(applicant, responseId);
    await initializeUnitSelection(propertyId, updatedForm, responseObject, activeResponse.leaseId);
    await initializePropertySettings(propertyId);
    await initializeSpecials();
    initializeAddons(responseObject);
    initializeWaitlistSelections(responseObject);
    updateAuthStore();

    return updatedForm;
  }

  async function fetchExistingResponse(responseId: string) {
    try {
      const response = await getResponse(responseId);
      return response?.data;
    } catch (e: any) {
      handleError(`Form Viewer - Error fetching responses: ${e}`, `Error fetching responses`);
      return {};
    }
  }

  function setSession(applicant: Applicant) {
    EntrataSession.setApplicant(applicant);
  }

  async function getFormWithPropertyIdentificationConfig(
    formData: Form,
    responseId: string,
  ): Promise<Form> {
    try {
      const application = await getApplicationForm(responseId);
      return mergePropertyIdentificationConfig(formData, application?.form);
    } catch {
      return formData;
    }
  }

  async function initializeApplication(data: Props) {
    const { applicant, form: formData, responseId, initMode = 'full' } = data;
    try {
      if (!responseId) {
        throw new Error('Response Id is missing.');
      }

      setSession(applicant);

      const initializedForm = await getFormWithPropertyIdentificationConfig(formData, responseId);
      const response = data.savedResponse ?? (await fetchExistingResponse(responseId));
      if (initMode === 'screeningMinimal') {
        const updatedForm = await initializeApplicationStore(
          applicant,
          initializedForm,
          responseId,
        );
        initializeNavigationStore(updatedForm, response);
        initializeResponseStore(response);
        await ensureScreeningConditionsInStore(responseId);
        updateAuthStore();
        return { form: updatedForm, applicant };
      }

      const updatedForm = await initializeStore(applicant, initializedForm, response, responseId);

      return { form: updatedForm, applicant };
    } catch (e: any) {
      throw new Error(e?.message ?? 'Error while initializing application');
    }
  }

  return initializeApplication;
};
